import { TIMEZONE_OPTIONS, findTimezoneOption } from "./timezones";

/**
 * Adapter between the account timezone picker and the zone list the app already
 * has.
 *
 * The list itself is NOT redefined here. `timezones.ts` already carries the full
 * Windows-style set with an alias table, and interview scheduling
 * (ScheduleInterviewModal, CreateEasyBookLinkModal, ApplicantDetailPanel) picks
 * zones from it today. A second list would drift from that one, and the two
 * would eventually disagree about what a user's zone is on the two screens that
 * matter most. This module only adds the few helpers the display seam needs on
 * top of it.
 */

/**
 * The shape of an IANA zone name: "UTC", or Area/Location with every segment
 * starting upper-case ("America/Argentina/Buenos_Aires", "Etc/GMT+5").
 *
 * Intl alone is not a sufficient check. It also accepts raw offsets ("+05:30"),
 * abbreviations ("EST") and any casing ("utc", "asia/kolkata"), and Postgres
 * reads a raw offset with the opposite sign: `AT TIME ZONE '+05:30'` lands 11
 * hours away from `AT TIME ZONE 'Asia/Kolkata'`. A stored "+05:30" would show
 * correct times on screen while the server cut every "Today" on the wrong day.
 *
 * Must stay identical to IANA_ZONE_SHAPE in server/utils/displayTimezone.ts and
 * to the copy inside displayTime.ts. A test compares all three.
 */
export const IANA_ZONE_SHAPE = /^(?:UTC|[A-Z][A-Za-z_+-]*(?:\/[A-Z][A-Za-z0-9_+-]*)+)$/;

/**
 * True iff `tz` is a canonically shaped IANA name that resolves on this runtime.
 * Mirrors the server's check.
 */
export function isValidIanaZone(tz: unknown): tz is string {
  if (typeof tz !== "string" || !IANA_ZONE_SHAPE.test(tz)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Options for the picker, mapped exactly as ScheduleInterviewModal maps them, so
 * a zone reads identically whether you are choosing your account default or an
 * interview slot.
 */
export function timezoneDropdownOptions(): { value: string; text: string }[] {
  return TIMEZONE_OPTIONS.map((tz) => ({ value: tz.value, text: tz.label }));
}

/**
 * One instant in each half of the year. Two zones are only treated as the same
 * clock when they agree at both, so a zone that observes daylight saving is
 * never matched to one that does not, even though they share an offset for half
 * the year.
 */
const PROBE_INSTANTS = [Date.UTC(2026, 0, 15, 12), Date.UTC(2026, 6, 15, 12)];

const wallClockCache = new Map<string, string>();

/** The wall clock a zone shows at both probe instants, as one comparable string. */
function wallClock(zone: string): string {
  let clock = wallClockCache.get(zone);
  if (clock === undefined) {
    clock = PROBE_INSTANTS.map((ms) =>
      new Date(ms).toLocaleString("en-US", { timeZone: zone, hour12: false }),
    ).join(" | ");
    wallClockCache.set(zone, clock);
  }
  return clock;
}

/**
 * The option value that stands for `zone`: its own option when the list has one
 * (through the alias table), otherwise the first option that keeps exactly the
 * same wall clock all year.
 *
 * The list is Windows-style and names one representative per offset rule, so 30
 * of the 418 zones this runtime reports have no option of their own, among
 * them Europe/Oslo, America/Louisville, Asia/Nicosia and Asia/Kuching. Before
 * this, a user in Oslo had no zone seeded, saw every time in UTC on every load,
 * and opened the picker to a blank field. Europe/Oslo shows the same clock as
 * the Europe/Berlin option in January and in July, so that is what they get.
 *
 * Returns "" when nothing matches, which is left to zones whose daylight-saving
 * rule no listed zone shares.
 */
export function offeredZoneFor(zone: string | null | undefined): string {
  if (!isValidIanaZone(zone)) return "";
  const own = findTimezoneOption(zone);
  if (own) return own.value;

  let target: string;
  try {
    target = wallClock(zone);
  } catch {
    return "";
  }
  const equivalent = TIMEZONE_OPTIONS.find((option) => {
    try {
      return wallClock(option.value) === target;
    } catch {
      return false;
    }
  });
  return equivalent?.value ?? "";
}

/**
 * The viewer's device zone, as an option the list offers, or "" when the runtime
 * reports nothing that maps to one.
 *
 * Deliberately NOT `resolveDefaultTimezone()` from timezones.ts, which floors an
 * unrecognised zone to America/New_York. That floor is right for a scheduling
 * field that must be non-empty, and wrong here: this value gets WRITTEN to the
 * user's stored preference, and silently recording an Indian user as Eastern is
 * worse than recording nothing and letting them pick.
 */
export function systemDisplayTimezone(): string {
  try {
    return offeredZoneFor(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return "";
  }
}

/**
 * The option value that represents `storedTz`, so the dropdown selects it rather
 * than rendering blank.
 *
 * Goes through the alias table because a stored zone and an offered zone are
 * often two names for one place: a browser reporting Asia/Calcutta has to select
 * the Asia/Kolkata option, and does not without this. A zone the list has no
 * option for selects the option with the same clock. Returns "" when the value
 * is empty or matches nothing, which shows the placeholder honestly instead of
 * pretending a zone is set.
 */
export function pickerSelectedValue(storedTz: string | null | undefined): string {
  return offeredZoneFor(storedTz);
}

/**
 * Compact label for a zone ("IST", "PST"), for the profile menu row where the
 * full "(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi" would not fit. Same
 * `short` field the interview modal shows. Falls back to the raw zone name.
 */
export function displayTimezoneLabel(zone: string | null | undefined): string {
  if (!zone) return "Not set";
  return findTimezoneOption(offeredZoneFor(zone) || zone)?.short ?? zone;
}
